import { useState } from "react";
import { MetricCard } from "./Ui";
import { BarList, ChartCard, ChartEmpty } from "./Charts";

export type LsaSummaryData = {
  calls: number;
  sorted: number;
  unsorted: number;
  booked: number;
  unqualified: number;
  qualified: number;
  booked_rate: number | null;
  callback_chances: number;
  turnarounds?: number;
  callbacks_open?: number;
  called_back?: number;
  needs_rating?: number;
  rated?: number;
  callback_emails?: number;
  dispositions: { disposition: string; label: string; calls: number }[];
  agents?: LsaAgent[];
  agents_unread?: number;
};

export type LsaAgent = {
  name: string | null;
  calls: number;
  qualified: number;
  booked: number;
  close_rate: number | null;
  avg_score: number | null;
  avg_hold_seconds: number | null;
};

const count = (n: number) => n.toLocaleString("en-US");
const share = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : "0%");
const hold = (s: number | null) => (s == null ? "—" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);

/**
 * The LSA calls in a date range by what really happened (Shawn, 2026-10-21):
 * booked rate of the calls that could book, calls that never could, call-back
 * chances, and calls still to rate in LSA (the team and owners both rate). The disposition list is the
 * check on a call center's own numbers.
 */
export default function LsaSummary({ data, period, agent, onAgent }: { data: LsaSummaryData; period?: string; agent?: string; onAgent?: (name: string) => void }) {
  const rating = (data.needs_rating || 0) + (data.rated || 0) > 0;
  const bars = data.dispositions.map((d) => ({ label: d.label, value: d.calls }));
  return (
    <div className="lsa-summary">
      <div className="module-metrics lsa-summary-metrics">
        {/* The dates themselves ("Oct 4 to Nov 2"): this page counts through today, the Ads pages through
            the last synced day, so "In these dates" left two different "Last 30 days" looking the same. */}
        <MetricCard label="Calls" value={count(data.calls)} detail={[period, data.unsorted ? `${count(data.unsorted)} not sorted yet` : ""].filter(Boolean).join(", ") || "In these dates"} />
        <MetricCard
          label="Booked"
          value={count(data.booked)}
          detail={data.booked_rate == null ? "No calls that could book" : `${data.booked_rate}% of ${count(data.qualified)} calls that could book`}
        />
        <MetricCard label="Could not book" value={count(data.unqualified)} detail="Wrong service, wrong area, spam or no conversation" />
        <MetricCard label="Call-back chances" value={count(data.callback_chances)} detail={data.callbacks_open != null && data.callbacks_open < data.callback_chances
          ? `${count(data.callbacks_open)} not called back yet` : "High or medium chance, not booked"} />
        {!!data.turnarounds && <MetricCard label="Turned around" value={count(data.turnarounds)} detail="Booked on a later follow-up call" />}
        {rating && <MetricCard label="To rate in LSA" value={count(data.needs_rating || 0)} detail={`${count(data.rated || 0)} rated so far`} />}
      </div>
      <ChartCard eyebrow="" title="What happened on the calls" meta={data.sorted ? data.sorted >= data.calls ? `All ${count(data.sorted)} calls` : `${count(data.sorted)} of ${count(data.calls)} calls sorted` : undefined}>
        {/* Each bar is a share of all sorted calls, so its length matches the % beside it
            (scaled to the biggest group, a 39% group drew a full bar). */}
        {bars.length ? (
          <BarList data={bars} limit={8} max={data.sorted || undefined} caption="LSA calls by what happened" format={(v) => `${count(v)} (${share(v, data.sorted)})`} />
        ) : (
          <ChartEmpty>No calls sorted in these dates yet. Each call is sorted once its recording is transcribed.</ChartEmpty>
        )}
      </ChartCard>
      {!!data.agents?.some((a) => a.name) && <LsaAgents agents={data.agents} unread={data.agents_unread || 0} agent={agent} onAgent={onAgent} />}
    </div>
  );
}

/**
 * Close rate by agent (Shawn, 2026-10-21 call): the same booked rate as the tiles,
 * for each person who answered, read from how they gave their name on the call.
 * It is the check on a call center's own per-agent numbers.
 */
function LsaAgents({ agents, unread, agent, onAgent }: { agents: LsaAgent[]; unread: number; agent?: string; onAgent?: (name: string) => void }) {
  const named = agents.filter((a) => a.name);
  const unnamed = agents.filter((a) => !a.name);
  // A busy call center has dozens of agents: the busiest first, the rest on request.
  const [all, setAll] = useState(false);
  const rows = [...(all ? named : named.slice(0, 8)), ...unnamed];
  // Hold time fills in on calls scanned since Oct 28; an empty column says nothing.
  const showHold = agents.some((a) => a.avg_hold_seconds != null);
  return (
    <ChartCard eyebrow="" title="Close rate by agent" meta={`Booked out of calls that could book${unread ? `. ${count(unread)} calls not read yet` : ""}`}>
      <div className="table-wrap">
        <table className="workflow-table lsa-agents">
          <thead><tr>
            <th>Agent</th>
            <th className="num">Calls</th>
            <th className="num">Could book</th>
            <th className="num">Booked</th>
            <th className="num">Close rate</th>
            <th className="num">Call score</th>
            {showHold && <th className="num">Hold</th>}
          </tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.name || "none"} className={a.name && a.name.toLowerCase() === agent?.toLowerCase() ? "is-selected" : undefined}>
                <td>{a.name
                  ? onAgent ? <button type="button" className="text-button" onClick={() => onAgent(a.name!)}>{a.name}</button> : a.name
                  : <span className="muted">{named.length ? "Name not said" : "No names read yet"}</span>}</td>
                <td className="num" data-label="Calls">{count(a.calls)}</td>
                <td className="num" data-label="Could book">{count(a.qualified)}</td>
                <td className="num" data-label="Booked">{count(a.booked)}</td>
                <td className="num" data-label="Close rate"><strong>{a.close_rate == null ? "—" : `${Math.round(a.close_rate)}%`}</strong></td>
                <td className="num" data-label="Call score">{a.avg_score == null ? "No score" : `${a.avg_score}/10`}</td>
                {showHold && <td className="num" data-label="Hold">{hold(a.avg_hold_seconds)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {named.length > 8 && <button type="button" className="text-button lsa-agents-more" onClick={() => setAll(!all)}>{all ? "Show the top 8" : `Show all ${count(named.length)} agents`}</button>}
    </ChartCard>
  );
}
